import { Component, OnInit, inject } from '@angular/core';
import { FormBuilder, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import {
  RunTransition,
  RunTransitionWithDialog,
  WorkflowService,
} from '../../services/workflow/workflow.service';
import { PayeePopupComponent } from '../../payee-popup/payee-popup.component';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatCheckbox } from '@angular/material/checkbox';
import { PayeeFormFieldComponent } from '../../payee-form-fields/payee-form-field/payee-form-field.component';
import { PayeeDropdownComponent } from '../../payee-form-fields/payee-dropdown/payee-dropdown.component';
import { PayeeInputDirective } from '../../payee-input/payee-input.directive';
import { PayeeIconButtonComponent } from '../../payee-buttons/payee-icon-button/payee-icon-button.component';
import { PayeeIconComponent } from '../../payee-icon/payee-icon.component';
import { PayeeButtonComponent } from '../../payee-buttons/payee-button/payee-button.component';
import { TranslateModule } from '@ngx-translate/core';

type DialogData = {
  transition_label: string;
  pro_forma_invoice_numbers: string[];
};

@Component({
  selector: 'app-book-bailiff-fee-paid-dialog',
  templateUrl: './book-bailiff-fee-paid-dialog.component.html',
  styleUrls: ['./book-bailiff-fee-paid-dialog.component.scss'],
  imports: [
    PayeePopupComponent,
    MatProgressSpinner,
    FormsModule,
    ReactiveFormsModule,
    MatCheckbox,
    PayeeFormFieldComponent,
    PayeeDropdownComponent,
    PayeeInputDirective,
    PayeeIconButtonComponent,
    PayeeIconComponent,
    PayeeButtonComponent,
    TranslateModule,
  ],
})
export class BookBailiffFeePaidDialogComponent implements OnInit {
  private fb = inject(FormBuilder);
  private data = inject<RunTransitionWithDialog>(MAT_DIALOG_DATA);
  private dialogRef =
    inject<MatDialogRef<BookBailiffFeePaidDialogComponent>>(MatDialogRef);
  private workflowService = inject(WorkflowService);

  readonly form = this.fb.group({
    link_pro_forma: this.fb.control(true, { nonNullable: true }),
    pro_forma: this.fb.control('', { nonNullable: true }),
  });

  get link_pro_forma() {
    return this.form.controls.link_pro_forma;
  }
  get pro_forma() {
    return this.form.controls.pro_forma;
  }

  loading = false;
  dialogData?: DialogData;

  async ngOnInit(): Promise<void> {
    try {
      this.dialogData = (await this.workflowService.getDialogData({
        caseId: this.data.caseId,
        transitionId: this.data.transitionId,
      })) as DialogData;

      if (this.dialogData.pro_forma_invoice_numbers.length > 0) {
        this.pro_forma.patchValue(this.dialogData.pro_forma_invoice_numbers[0]);
      }
    } catch (error) {
      this.workflowService.showTransitionError(error);
      this.dialogRef.close();
    }
  }

  clearProForma(): void {
    this.pro_forma.patchValue('');
  }

  readonly onSubmit = async () => {
    if (this.form.invalid) {
      return;
    }

    const { link_pro_forma, pro_forma } = this.form.getRawValue();

    const params: Record<string, unknown> = {
      ...(this.data.extraParams ?? {}),
    };

    if (link_pro_forma && pro_forma) {
      params.pro_forma_invoice_number = pro_forma;
    }

    const closeValue: RunTransition = {
      transitionId: this.data.transitionId,
      caseId: this.data.caseId,
      params,
    };

    this.loading = true;
    try {
      await this.workflowService.runTransition(closeValue);
      this.dialogRef.close(closeValue);
    } catch (error) {
      this.workflowService.showTransitionError(error);
    } finally {
      this.loading = false;
    }
  };

  readonly onCancel = () => {
    this.dialogRef.close();
  };
}
